<template>
  <div class="dashboard-container">
    <!-- HEADER SECTION -->
    <b-card class="mb-4 dashboard-header" no-body>
      <b-card-body class="d-flex justify-content-between align-items-center py-3">
        <div>
          <div class="d-flex align-items-center">
            <div class="header-icon-wrapper mr-3">
              <b-icon-shop class="header-icon" />
            </div>
            <div>
              <h5 class="mb-0 header-title">STORES REPORT</h5>
              <small class="header-subtitle">Track store performance and sales analytics</small>
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center">
          <b-badge variant="light" class="mr-2 date-badge">
            <b-icon-calendar class="mr-1" />{{ formattedDateRange }}
          </b-badge>
          <b-badge variant="primary" pill class="animated-badge">
            {{ filteredItems.length }} {{ filteredItems.length === 1 ? 'Record' : 'Records' }}
          </b-badge>
        </div>
      </b-card-body>
    </b-card>

    <!-- FILTERS CARD -->
    <b-card class="mb-4 filters-card" no-body>
      <b-card-body class="py-3">
        <div class="row align-items-end">
          <div class="col-lg-8">
            <div class="d-flex flex-wrap align-items-end" style="gap: 16px;">
              <div style="min-width: 320px;">
                <label class="small text-muted mb-2 filter-label">
                  <b-icon-calendar class="mr-1" size="sm" />DATE RANGE
                </label>
                <date-picker
                  v-model="date_range"
                  format="MM-DD-YYYY"
                  valueType="format"
                  class="custom-datepicker"
                  placeholder="Select Date Range"
                  range
                  :class="{ 'datepicker-focused': dateRangeFocused }"
                  @focus="dateRangeFocused = true"
                  @blur="dateRangeFocused = false"
                ></date-picker>
              </div>

              <div class="d-flex flex-wrap" style="gap: 8px;">
                <b-button
                  v-for="btn in quickDateButtons"
                  :key="btn.id"
                  size="md"
                  :variant="picked_date_range === btn.id ? 'danger' : 'outline-danger'"
                  @click="set_dates(btn.id)"
                  class="quick-date-btn py-2 px-3"
                >
                  <b-icon v-if="picked_date_range === btn.id" :icon="btn.icon" class="mr-1" size="sm" />
                  {{ btn.label }}
                </b-button>
              </div>
            </div>
          </div>

          <div class="col-lg-4 mt-3 mt-lg-0">
            <div class="d-flex justify-content-end">
              <b-button
                variant="success"
                @click="fetch_init"
                class="search-btn"
                :disabled="items_being_loaded"
              >
                <b-icon-search class="mr-2" />
                <span v-if="!items_being_loaded">SEARCH</span>
                <span v-else>
                  <b-icon-arrow-repeat spin class="mr-2" />LOADING...
                </span>
              </b-button>
            </div>
          </div>
        </div>

        <!-- ADVANCED FILTERS -->
        <div class="mt-3">
          <b-button
            variant="link"
            size="md"
            @click="showAdvancedFilters = !showAdvancedFilters"
            class="p-0 text-decoration-none advanced-toggle"
          >
            <b-icon :icon="showAdvancedFilters ? 'chevron-up' : 'chevron-down'" class="mr-1" size="sm" />
            {{ showAdvancedFilters ? 'Hide' : 'Show' }} Filters
          </b-button>

          <b-collapse v-model="showAdvancedFilters">
            <div class="mt-3 pt-3 border-top">
              <div class="row">
                <div class="col-md-3 mb-3">
                  <label class="small text-muted mb-2 filter-label">
                    <b-icon-file-text class="mr-1" size="sm" />REPORT TYPE
                  </label>
                  <b-form-select
                    :options="details"
                    v-model="details_filter"
                    size="md"
                    class="filter-select"
                    @change="fetch_init"
                  >
                    <template #first>
                      <b-form-select-option :value="null">SELECT ANY</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>

                <div class="col-md-3 mb-3">
                  <label class="small text-muted mb-2 filter-label">
                    <b-icon-building class="mr-1" size="sm" />LOCATION
                  </label>
                  <b-form-select
                    :options="filteredLocations"
                    v-model="picked_location_id"
                    size="md"
                    class="filter-select"
                    @change="fetch_init"
                  >
                    <template #first>
                      <b-form-select-option :value="null">All Locations</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>

                <div class="col-md-3 mb-3">
                  <label class="small text-muted mb-2 filter-label">
                    <b-icon-receipt class="mr-1" size="sm" />INVOICE TYPE
                  </label>
                  <b-form-select
                    :options="invoice_options"
                    v-model="picked_invoice_type"
                    size="md"
                    class="filter-select"
                    @change="fetch_init"
                  >
                    <template #first>
                      <b-form-select-option :value="null">All Types</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>

                <div class="col-md-3 mb-3">
                  <label class="small text-muted mb-2 filter-label">
                    <b-icon-tag class="mr-1" size="sm" />SALE TYPE
                  </label>
                  <b-form-select
                    :options="sale_type_options"
                    v-model="picked_sale_type"
                    size="md"
                    class="filter-select"
                    @change="fetch_init"
                  >
                    <template #first>
                      <b-form-select-option :value="null">All Sale Types</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>
              </div>
            </div>
          </b-collapse>
        </div>
      </b-card-body>
    </b-card>

    <!-- LOADING STATE -->
    <div v-if="items_being_loaded" class="text-center py-5">
      <b-spinner variant="primary" type="grow" label="Loading..." style="width: 3rem; height: 3rem;"></b-spinner>
      <p class="mt-3 text-muted" style="font-size: 18px;">Fetching store data...</p>
    </div>

    <!-- MAIN CONTENT -->
    <template v-else-if="filteredItems.length > 0">
      <!-- ============================================== -->
      <!-- SUMMARY STAT CARDS - REFERENCE STYLE (OPTION A) -->
      <!-- ============================================== -->
      <div v-if="details_filter === 1" class="kpi-grid">
        <!-- Card 1: Total Sales (DARK) -->
        <div class="kpi-card dark accent-green">
          <div class="kpi-icon-wrap icon-green">
            <b-icon-graph-up />
          </div>
          <span class="kpi-label">Total Sales</span>
          <span class="kpi-value">{{ summaryStats[0] ? summaryStats[0].value : '$0.00' }}</span>
          <div class="kpi-trend trend-green">
            <b-icon-receipt />
            {{ filteredItems.length }} locations
          </div>
        </div>

        <!-- Card 2: Total Refunds -->
        <div class="kpi-card accent-red">
          <div class="kpi-icon-wrap icon-red">
            <b-icon-arrow-return-left />
          </div>
          <span class="kpi-label">Total Refunds</span>
          <span class="kpi-value">{{ summaryStats[1] ? summaryStats[1].value : '$0.00' }}</span>
          <div class="kpi-trend trend-red">
            <b-icon-graph-down-arrow />
            vs sales
          </div>
        </div>

        <!-- Card 3: Net Amount -->
        <div class="kpi-card accent-purple">
          <div class="kpi-icon-wrap icon-purple">
            <b-icon-wallet />
          </div>
          <span class="kpi-label">Net Amount</span>
          <span class="kpi-value">{{ summaryStats[2] ? summaryStats[2].value : '$0.00' }}</span>
          <div class="kpi-trend trend-purple">
            <b-icon-percent />
            Sales − Refunds
          </div>
        </div>

        <!-- Card 4: Sales Tax -->
        <div class="kpi-card accent-amber">
          <div class="kpi-icon-wrap icon-amber">
            <b-icon-receipt />
          </div>
          <span class="kpi-label">Sales Tax</span>
          <span class="kpi-value">{{ summaryStats[3] ? summaryStats[3].value : '$0.00' }}</span>
          <div class="kpi-trend trend-amber">
            <b-icon-cash />
            collected
          </div>
        </div>
      </div>

      <!-- DATA TABLE SECTION -->
      <b-card no-body class="mt-3 data-table-card">
        <b-card-header class="bg-light d-flex justify-content-between align-items-center py-2">
          <div class="d-flex align-items-center">
            <b-icon-table class="mr-2" size="sm" />
            <h6 class="mb-0 table-title">{{ getTableTitle() }}</h6>
            <b-badge variant="secondary" class="ml-2 table-badge">{{ filteredItems.length }} {{ filteredItems.length === 1 ? 'record' : 'records' }}</b-badge>
          </div>
          <div>
            <b-button v-if="packet.userDetails.userrole == 'admin'" size="md" variant="outline-secondary" @click="exportToCSV" class="mr-2">
              <b-icon-download class="mr-1" size="sm" /> Export
            </b-button>
            <b-button size="md" variant="outline-primary" @click="fetch_init">
              <b-icon-arrow-repeat class="mr-1" size="sm" /> Refresh
            </b-button>
          </div>
        </b-card-header>
        <b-card-body class="p-0">
          <b-overlay :show="items_being_loaded" spinner-variant="primary" spinner-type="grow" spinner-small rounded="sm">
            <div class="table-wrapper-sm">
              <table class="compact-table">
                <thead>
                  <tr>
                    <th v-for="col in allowed_cols()" :key="col"
                        :class="getHeaderClass(col)"
                        :style="getHeaderStyle(col)">
                      <div class="d-flex align-items-center justify-content-between">
                        <span>{{ (fixed_columns_dict[col] || col) }}</span>
                        <b-button
                          v-if="isSortableColumn(col)"
                          variant="link"
                          size="sm"
                          class="p-0 ml-1 text-muted sort-btn"
                          @click="sortByColumn(col)"
                        >
                          <b-icon-arrow-up-short v-if="sortColumn === col && sortDirection === 'asc'" size="sm" />
                          <b-icon-arrow-down-short v-else-if="sortColumn === col && sortDirection === 'desc'" size="sm" />
                          <b-icon-arrow-up-short v-else size="sm" class="text-muted" style="opacity: 0.3;" />
                        </b-button>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <!-- DATA ROWS -->
                  <tr v-for="(item, idx) in paginatedItems" :key="idx"
                      :class="getRowClass(item)">
                    <td v-for="row_col in allowed_cols()" :key="row_col"
                        :class="getCellClass(row_col, item[row_col])">
                      <span v-if="row_col == 'locsub'">
                        {{ packet.locsubsdict[item[row_col]] || item[row_col] }}
                      </span>
                      <span v-else-if="['invoice_id', 'lease_ids', 'lease_company'].includes(row_col)">
                        {{ item[row_col] || '—' }}
                      </span>
                      <span v-else-if="row_col.startsWith('customer_')">
                        {{ item[row_col] || '—' }}
                      </span>
                      <span v-else-if="row_col == 'is_delivered'">
                        <b-badge :variant="item[row_col] ? 'success' : 'warning'" pill class="delivery-badge">
                          {{ item[row_col] ? 'Delivered' : 'Not Delivered' }}
                        </b-badge>
                      </span>
                      <span v-else-if="isNumericColumn(row_col)">
                        {{ formatCurrency(item[row_col]) }}
                      </span>
                      <span v-else>
                        {{ item[row_col] !== null && item[row_col] !== undefined ? item[row_col] : '—' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
                <!-- Footer with Totals -->
                <tfoot v-if="showFooter" class="totals-row">
                  <tr>
                    <td v-for="col in allowed_cols()" :key="'footer-' + col"
                        class="fw-bold">
                      <span v-if="col === 'locsub'">TOTAL</span>
                      <span v-else-if="isNumericColumn(col)">
                        {{ formatCurrency(columnTotal(col)) }}
                      </span>
                      <span v-else>—</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="filteredItems.length > perPage" class="pagination-wrapper d-flex justify-content-between align-items-center p-3 bg-light">
              <small class="text-muted" style="font-size: 16px;">
                Showing {{ ((currentPage - 1) * perPage) + 1 }} to {{ Math.min(currentPage * perPage, filteredItems.length) }} of {{ filteredItems.length }} records
              </small>
              <b-pagination
                v-model="currentPage"
                :total-rows="filteredItems.length"
                :per-page="perPage"
                size="md"
                class="mb-0"
                align="center"
              ></b-pagination>
              <b-form-select v-model="perPage" size="md" style="width: 85px;" @change="currentPage = 1">
                <option :value="10">10</option>
                <option :value="25">25</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
              </b-form-select>
            </div>
          </b-overlay>
        </b-card-body>
      </b-card>
    </template>

    <!-- EMPTY STATE -->
    <div v-else-if="!items_being_loaded && items.length === 0" class="text-center py-5">
      <b-alert show variant="light" class="mb-0" style="font-size: 18px;">
        <b-icon-inbox class="mr-2" />
        No records found. Select a date range and click SEARCH.
      </b-alert>
    </div>

    <div v-else-if="!items_being_loaded && filteredItems.length === 0 && items.length > 0" class="text-center py-5">
      <b-alert show variant="info" class="mb-0" style="font-size: 18px;">
        <b-icon-filter-circle class="mr-2" />
        No data matches the selected filters. Try adjusting your filters.
      </b-alert>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';
import 'moment/locale/en-gb';
import global_vars from '@/utilities/global_vars';
import Backer from '@/components/globals/Backer';
import Requestor from '@/utilities/Requestor';
import JsonCSV from 'vue-json-csv';

import {
  BIconShop,
  BIconCalendar,
  BIconSearch,
  BIconTable,
  BIconArrowRepeat,
  BIconDownload,
  BIconBuilding,
  BIconTag,
  BIconInbox,
  BIconFileText,
  BIconReceipt,
  BIconArrowUpShort,
  BIconArrowDownShort,
  BIconFilterCircle,
  BIconChevronUp,
  BIconChevronDown,
  BIconPerson,
  BIconGraphUp,
  BIconGraphDownArrow,
  BIconWallet,
  BIconCash,
  BIconPercent
} from 'bootstrap-vue';

moment.locale('en-gb');

export default {
  name: "ReportsStoresSale",
  components: {
    Backer,
    DatePicker,
    JsonCSV,
    BIconShop,
    BIconCalendar,
    BIconSearch,
    BIconTable,
    BIconArrowRepeat,
    BIconDownload,
    BIconBuilding,
    BIconTag,
    BIconInbox,
    BIconFileText,
    BIconReceipt,
    BIconArrowUpShort,
    BIconArrowDownShort,
    BIconFilterCircle,
    BIconChevronUp,
    BIconChevronDown,
    BIconPerson,
    BIconGraphUp,
    BIconGraphDownArrow,
    BIconWallet,
    BIconCash,
    BIconPercent
  },
  data: function() {
    return {
      packet: {},
      items: [],
      items_being_loaded: false,
      global_vars: global_vars,
      date_range: [moment().startOf('month').format("MM-DD-YYYY"), moment().format("MM-DD-YYYY")],
      picked_date_range: 'this_month',
      dateRangeFocused: false,
      showAdvancedFilters: false,

      sale_type_options: [
        { value: null, text: "ALL SALE TYPES" },
        { value: 'regular', text: "REGULAR SALES" },
        { value: 'layaway', text: "LAYAWAY SALES" }
      ],
      picked_sale_type: null,
      details: [
        { value: 1, text: "SUMMARY" },
        { value: 2, text: "DETAILED REFUNDS" },
        { value: 3, text: "DETAILED SALES" },
        { value: 4, text: "PER INVOICE SALES" },
      ],
      invoice_options: [
        { value: null, text: "ALL" },
        { value: 1, text: "WRITTEN" },
        { value: 2, text: "DELIVERED" },
        { value: 3, text: "NOT DELIVERED" },
      ],

      fixed_columns_dict: {
        locsub: "LOCATION",
        total_without_tax: "TOTAL SALE",
        total_refunded_without_tax: "TOTAL REFUNDED",
        total_sales_tax: "SLS TAX",
        total_refunded_st: "SLS TAX REFUNDED",
        total_cc_sales: "CC SALES",
        total_finance_sales: "FIN SALES",
        total_tax_exempt_sales: "TAX EXEMPT SALES",
        total_cash_sales: "CASH SALES",
        total_cc_refunds: "CC REFUNDS",
        total_finance_refunds: "FIN REFUNDS",
        total_tax_exempt_refunds: "TAX EXEMPT REFUNDS",
        total_cash_refunds: "CASH REFUNDS",
        invoice_id: "INVOICE #",
        lease_ids: "LEASE IDS",
        lease_company: "LEASE COMPANY",
        customer_fullname: "CUSTOMER NAME",
        customer_email: "EMAIL",
        customer_phone: "PHONE",
        customer_phone_2: "PHONE 2",
        customer_address_line_1: "ADDRESS LINE 1",
        customer_address_line_2: "ADDRESS LINE 2",
        customer_city: "CITY",
        customer_state: "STATE",
        customer_zipcode: "ZIPCODE",
        is_delivered: "DELIVERY STATUS"
      },
      columns: [],
      picked_location_id: null,
      details_filter: 1,
      picked_invoice_type: null,
      currentPage: 1,
      perPage: 25,
      sortColumn: null,
      sortDirection: 'asc',

      quickDateButtons: [
        { id: 'today', label: 'Today', icon: 'calendar-day' },
        { id: 'yesterday', label: 'Yesterday', icon: 'calendar-day' },
        { id: 'this_week', label: 'This Week', icon: 'calendar-week' },
        { id: 'last_week', label: 'Last Week', icon: 'calendar-week' },
        { id: 'this_month', label: 'This Month', icon: 'calendar-month' },
        { id: 'last_month', label: 'Last Month', icon: 'calendar-month' }
      ],
    }
  },
  computed: {
    formattedDateRange() {
      if (this.date_range && this.date_range.length === 2) {
        return `${this.date_range[0]} - ${this.date_range[1]}`;
      }
      return 'Select dates';
    },

    filteredLocations() {
      if (!this.packet.locations) return [];
      const excludedLocationNames = [
        'FOLCROFT WH',
        '101 WH',
        '9TH ST WH',
        'OUT OF STOCK',
        'MECCA SHIPPING',
        'ONLINE SALES'
      ];
      return this.packet.locations.filter(location =>
        !excludedLocationNames.includes(location.text)
      );
    },

    filteredItems() {
      let filtered = this.items.filter(item => this.allowed_row(item));

      if (this.sortColumn) {
        filtered = filtered.sort((a, b) => {
          let aVal = a[this.sortColumn];
          let bVal = b[this.sortColumn];

          if (this.isNumericColumn(this.sortColumn)) {
            aVal = parseFloat(aVal || 0);
            bVal = parseFloat(bVal || 0);
          }

          if (this.sortColumn === 'locsub') {
            aVal = this.packet.locsubsdict[aVal] || aVal;
            bVal = this.packet.locsubsdict[bVal] || bVal;
          }

          if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
          if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
          return 0;
        });
      }

      return filtered;
    },

    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredItems.slice(start, end);
    },

    numericColumns() {
      return ['total_without_tax', 'total_refunded_without_tax', 'total_sales_tax',
             'total_refunded_st', 'total_cc_sales', 'total_finance_sales',
             'total_tax_exempt_sales', 'total_cash_sales', 'total_cc_refunds',
             'total_finance_refunds', 'total_tax_exempt_refunds', 'total_cash_refunds'];
    },

    summaryStats() {
      if (this.filteredItems.length === 0 || this.details_filter !== 1) return [];

      const totals = this.filteredItems.reduce((acc, item) => {
        acc.totalSales += parseFloat(item.total_without_tax || 0);
        acc.totalRefunds += parseFloat(item.total_refunded_without_tax || 0);
        acc.netAmount += (parseFloat(item.total_without_tax || 0) - parseFloat(item.total_refunded_without_tax || 0));
        acc.salesTax += parseFloat(item.total_sales_tax || 0);
        return acc;
      }, { totalSales: 0, totalRefunds: 0, netAmount: 0, salesTax: 0 });

      return [
        {
          label: 'Total Sales',
          value: this.formatCurrency(totals.totalSales),
          style: 'border-left: 4px solid #10b981;'
        },
        {
          label: 'Total Refunds',
          value: this.formatCurrency(totals.totalRefunds),
          style: 'border-left: 4px solid #ef4444;'
        },
        {
          label: 'Net Amount',
          value: this.formatCurrency(totals.netAmount),
          style: 'border-left: 4px solid #6366f1;'
        },
        {
          label: 'Sales Tax',
          value: this.formatCurrency(totals.salesTax),
          style: 'border-left: 4px solid #f59e0b;'
        }
      ];
    },

    showFooter() {
      return this.details_filter !== 4 && this.filteredItems.length > 0;
    }
  },
  watch: {
    filteredItems() {
      this.currentPage = 1;
    }
  },
  methods: {
    formatCurrency(value) {
      const num = parseFloat(value || 0);
      if (isNaN(num)) return '$0.00';
      return num.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    allowed_cols() {
      if (!this.columns.length) return [];

      let cols = [];
      switch (this.details_filter) {
        case 1:
          cols = ['locsub', 'total_without_tax', 'total_refunded_without_tax',
                 'total_sales_tax', 'total_refunded_st', 'total_cash_sales',
                 'total_cc_sales', 'total_finance_sales', 'total_tax_exempt_sales'];
          break;
        case 2:
          cols = ['locsub', 'total_without_tax', 'total_refunded_without_tax',
                 'total_sales_tax', 'total_refunded_st'];
          cols = cols.concat(this.columns.filter(col =>
            col.startsWith('total_') && col.endsWith('_refunds')
          ));
          cols = cols.concat(this.columns.filter(col =>
            col.startsWith('REFUNDS (')
          ));
          break;
        case 3:
          cols = ['locsub', 'total_without_tax', 'total_refunded_without_tax',
                 'total_sales_tax', 'total_refunded_st'];
          cols = cols.concat(this.columns.filter(col =>
            col.startsWith('total_') && col.endsWith('_sales')
          ));
          cols = cols.concat(this.columns.filter(col =>
            col.startsWith('SALES (')
          ));
          break;
        case 4:
          cols = [
            'invoice_id', 'customer_fullname', 'is_delivered', 'lease_ids',
            'lease_company', 'locsub', 'total_without_tax',
            'total_refunded_without_tax', 'total_sales_tax', 'total_refunded_st',
            'total_cash_sales', 'total_cc_sales', 'total_finance_sales',
            'total_tax_exempt_sales'
          ];
          cols = cols.concat(this.columns.filter(col =>
            col.startsWith('SALES (')
          ));
          const additionalCustomerColumns = [
            'customer_email', 'customer_phone', 'customer_phone_2',
            'customer_address_line_1', 'customer_address_line_2',
            'customer_city', 'customer_state', 'customer_zipcode'
          ];
          cols = cols.concat(additionalCustomerColumns.filter(col => this.columns.includes(col)));
          break;
        default:
          cols = ['locsub', 'total_without_tax', 'total_refunded_without_tax'];
      }

      return cols.filter(col => this.columns.includes(col));
    },

    allowed_row(item) {
      if (this.picked_location_id != null) {
        return item.location_id == this.picked_location_id;
      }
      return true;
    },

    set_dates(value) {
      let start = null;
      let end = null;

      switch(value) {
        case 'today':
          start = moment().format("MM-DD-YYYY");
          end = moment().format("MM-DD-YYYY");
          break;
        case 'yesterday':
          start = moment().add(-1, 'days').format("MM-DD-YYYY");
          end = moment().add(-1, 'days').format("MM-DD-YYYY");
          break;
        case 'this_week':
          start = moment().startOf('week').format("MM-DD-YYYY");
          end = moment().endOf('week').format("MM-DD-YYYY");
          break;
        case 'last_week':
          start = moment().subtract(1, 'weeks').startOf('week').format("MM-DD-YYYY");
          end = moment().subtract(1, 'weeks').endOf('week').format("MM-DD-YYYY");
          break;
        case 'this_month':
          start = moment().startOf('month').format("MM-DD-YYYY");
          end = moment().endOf('month').format("MM-DD-YYYY");
          break;
        case 'last_month':
          start = moment().subtract(1, 'months').startOf('month').format("MM-DD-YYYY");
          end = moment().subtract(1, 'months').endOf('month').format("MM-DD-YYYY");
          break;
      }

      if (start && end) {
        this.date_range = [start, end];
        this.picked_date_range = value;
        this.fetch_init();
      }
    },

    fetch_init() {
      if (!this.details_filter) {
        this.$bvToast.toast('Please select a report type.', {
          title: 'Invalid Filter',
          variant: 'warning',
          solid: true
        });
        return;
      }

      if (!this.date_range || this.date_range.length !== 2) {
        this.$bvToast.toast('Please select a date range.', {
          title: 'Invalid Date Range',
          variant: 'warning',
          solid: true
        });
        return;
      }

      let location_id = this.packet.userDetails.properties.location_id;
      if (this.packet.userDetails.userrole == 'admin') {
        location_id = null;
      }

      let that = this;
      let data = {
        dates: this.date_range,
        location_id: location_id,
        picked_invoice_type: this.picked_invoice_type,
        details_filter: this.details_filter,
        sale_type: this.picked_sale_type
      };

      let path = "/api/reports/reports_stores_sale/";

      let s = function(response) {
        if (response.data.result) {
          that.items = response.data.result;
          that.derive_sorted_columns();
          that.$bvToast.toast(
            `Loaded ${that.items.length} records`,
            { title: "Success", autoHideDelay: 1500, variant: "success" }
          );
        } else {
          that.$bvToast.toast(
            response.data.error || "Unknown error occurred",
            { title: "Error", autoHideDelay: 1500, variant: "warning" }
          );
        }
        that.items_being_loaded = false;
      }

      let f = function(error) {
        console.error('API Error:', error);
        that.$bvToast.toast(
          "Error occurred. Please try again.",
          { title: "Error", autoHideDelay: 1500, variant: "danger" }
        );
        that.items_being_loaded = false;
      }

      this.items_being_loaded = true;
      this.items = [];
      Requestor.postSyncP(path, data, this, s, f);
    },

    derive_sorted_columns() {
      if (this.items.length > 0) {
        let dcs = Object.keys(this.items[0]);

        let columns = ["locsub", "total_without_tax", "total_refunded_without_tax",
                     "total_sales_tax", "total_refunded_st"];

        let salesColumns = dcs.filter(col =>
          col.startsWith('total_') && col.endsWith('_sales')
        );
        columns = columns.concat(salesColumns);

        let refundColumns = dcs.filter(col =>
          col.startsWith('total_') && col.endsWith('_refunds')
        );
        columns = columns.concat(refundColumns);

        let financeSales = dcs.filter(col => col.startsWith('SALES ('));
        columns = columns.concat(financeSales);

        let financeRefunds = dcs.filter(col => col.startsWith('REFUNDS ('));
        columns = columns.concat(financeRefunds);

        let invoiceColumns = ['invoice_id', 'lease_ids', 'lease_company', 'is_delivered'];
        columns = columns.concat(invoiceColumns.filter(col => dcs.includes(col)));

        let customerColumns = dcs.filter(col => col.startsWith('customer_'));
        columns = columns.concat(customerColumns);

        this.columns = columns;
      } else {
        this.columns = [];
      }
    },

    sortByColumn(col) {
      if (this.sortColumn === col) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = col;
        this.sortDirection = 'asc';
      }
    },

    isSortableColumn(col) {
      return !['invoice_id', 'lease_ids', 'lease_company'].includes(col);
    },

    exportToCSV() {
      if (this.filteredItems.length === 0) {
        this.$bvToast.toast('No data to export', { title: 'Info', variant: 'info' });
        return;
      }

      const headers = this.allowed_cols().map(col => this.fixed_columns_dict[col] || col);
      const rows = this.filteredItems.map(item => {
        return this.allowed_cols().map(col => {
          if (col === 'locsub') {
            return this.packet.locsubsdict[item[col]] || item[col] || '';
          } else if (col === 'is_delivered') {
            return item[col] ? 'Delivered' : 'Not Delivered';
          } else if (this.isNumericColumn(col)) {
            const num = item[col] != null ? parseFloat(item[col]) : 0;
            return num.toFixed(2);
          } else {
            return item[col] || '';
          }
        });
      });

      const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.getTableTitle().toUpperCase().replace(/\s/g, '_')}_${moment().format('YYYY-MM-DD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      this.$bvToast.toast('Export completed!', { title: 'Success', autoHideDelay: 1500, variant: 'success' });
    },

    getTableTitle() {
      const titles = {
        1: 'Sales Summary',
        2: 'Detailed Refunds',
        3: 'Detailed Sales',
        4: 'Per Invoice Sales'
      };
      return titles[this.details_filter] || 'Store Reports';
    },

    getHeaderClass(col) {
      return this.isNumericColumn(col) ? 'text-right' : 'text-center';
    },

    getHeaderStyle(col) {
      if (col === 'locsub') return 'min-width: 180px;';
      if (['invoice_id', 'lease_ids', 'lease_company'].includes(col)) return 'min-width: 120px;';
      if (col.startsWith('customer_')) return 'min-width: 150px;';
      if (col === 'is_delivered') return 'min-width: 120px;';
      return 'min-width: 100px;';
    },

    getCellClass(col, value) {
      let classes = [];
      if (this.isNumericColumn(col)) classes.push('text-right');
      if (col.includes('refund') && parseFloat(value) > 0) classes.push('text-danger');
      if (col.includes('sales') && parseFloat(value) > 0) classes.push('text-success');
      if (col.includes('tax_exempt') && parseFloat(value) > 0) classes.push('text-tax-exempt');
      if (col === 'is_delivered') classes.push('text-center');
      return classes.join(' ');
    },

    getRowClass(item) {
      const netAmount = parseFloat(item.total_without_tax || 0) - parseFloat(item.total_refunded_without_tax || 0);
      if (netAmount < 0) return 'table-danger-row';
      if (netAmount > 10000) return 'table-success-row';
      return '';
    },

    isNumericColumn(col) {
      const nonNumericCols = ['locsub', 'invoice_id', 'lease_ids', 'lease_company', 'is_delivered'];
      if (col.startsWith('customer_')) return false;
      return !nonNumericCols.includes(col);
    },

    columnTotal(col) {
      if (!this.isNumericColumn(col)) return 0;
      return this.filteredItems.reduce((sum, item) => {
        const value = parseFloat(item[col] || 0);
        return isNaN(value) ? sum : sum + value;
      }, 0);
    }
  },
  created() {
    this.packet = JSON.parse(localStorage.packet || '{}');
    this.packet.userDetails = JSON.parse(localStorage.userDetails || '{}');
    this.$nextTick(() => {
      this.fetch_init();
    });
  }
}
</script>

<style scoped>
/* =========================================================
   GLOBAL
   ========================================================= */
.dashboard-container {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
  padding: 24px;
}

/* =========================================================
   HEADER
   ========================================================= */
.dashboard-header {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: white;
  border: none;
  border-radius: 18px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15);
}

.header-icon-wrapper {
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon {
  font-size: 28px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
}

.header-title {
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.header-subtitle {
  font-size: 16px;
  opacity: 0.8;
}

.date-badge {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
}

.animated-badge {
  animation: fadeIn 0.5s ease-out;
  font-size: 14px;
  padding: 8px 16px;
}

/* =========================================================
   FILTERS
   ========================================================= */
.filters-card {
  border: none;
  border-radius: 18px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  background: white;
}

.filter-label {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.custom-datepicker {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px 16px;
  font-size: 18px;
  width: 100%;
}

.datepicker-focused {
  border-color: #6366f1;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.15);
}

.quick-date-btn {
  border-radius: 20px;
  font-size: 16px;
  padding: 10px 18px;
  font-weight: 500;
}

.filter-select {
  border-radius: 12px;
  background: #f8fafc;
  border-color: #e2e8f0;
  font-size: 18px;
  padding: 10px 16px;
}

.search-btn {
  border-radius: 12px;
  padding: 12px 36px;
  font-weight: 700;
  font-size: 18px;
  letter-spacing: 0.5px;
}

.advanced-toggle {
  font-size: 16px;
  font-weight: 500;
}

/* =========================================================
   KPI CARDS - REFERENCE STYLE (OPTION A)
   ========================================================= */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 24px;
}

.kpi-card {
  background: #fff;
  border-radius: 20px;
  padding: 36px 24px 30px;
  border: 1px solid #e8edf3;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  position: relative;
  overflow: hidden;
}

.kpi-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
}

.kpi-card:hover {
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.12);
  transform: translateY(-2px);
}

.kpi-card.dark {
  background: #0b1220;
  border-color: #0b1220;
  box-shadow: 0 8px 26px rgba(11, 18, 32, 0.35);
}

.kpi-card.dark .kpi-label {
  color: rgba(255, 255, 255, 0.65);
}

.kpi-card.dark .kpi-value {
  color: #ffffff;
}

/* Accent stripe colors */
.accent-green::before   { background: linear-gradient(90deg, #10b981, #34d399); }
.accent-red::before     { background: linear-gradient(90deg, #ef4444, #f87171); }
.accent-purple::before  { background: linear-gradient(90deg, #8b5cf6, #a78bfa); }
.accent-amber::before   { background: linear-gradient(90deg, #f59e0b, #fbbf24); }

/* Icon box */
.kpi-icon-wrap {
  width: 76px;
  height: 76px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 34px;
  margin-bottom: 22px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.icon-green   { background: linear-gradient(135deg, #10b981, #34d399); }
.icon-red     { background: linear-gradient(135deg, #ef4444, #f87171); }
.icon-purple  { background: linear-gradient(135deg, #8b5cf6, #a78bfa); }
.icon-amber   { background: linear-gradient(135deg, #f59e0b, #fbbf24); }

/* Label */
.kpi-label {
  font-size: 18px;
  color: #64748b;
  font-weight: 500;
  margin-bottom: 12px;
  letter-spacing: 0.2px;
}

/* Big value */
.kpi-value {
  font-size: 40px;
  font-weight: 800;
  color: #0b1220;
  line-height: 1.1;
  letter-spacing: -1.5px;
  margin-bottom: 18px;
  word-break: break-word;
}

/* Trend pill badge */
.kpi-trend {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  padding: 8px 18px;
  border-radius: 999px;
  white-space: nowrap;
}

.trend-green   { color: #047857; background: #d1fae5; }
.trend-red     { color: #b91c1c; background: #fee2e2; }
.trend-purple  { color: #6d28d9; background: #ede9fe; }
.trend-amber   { color: #92400e; background: #fef3c7; }

.kpi-card.dark .kpi-trend.trend-green {
  color: #34d399;
  background: rgba(16, 185, 129, 0.18);
}

/* =========================================================
   DATA TABLE
   ========================================================= */
.data-table-card {
  border: none;
  border-radius: 18px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.table-title {
  font-size: 20px;
  font-weight: 600;
}

.table-badge {
  font-size: 14px;
  padding: 6px 12px;
}

.table-wrapper-sm {
  overflow-x: auto;
}

.compact-table {
  width: 100%;
  font-size: 16px;
  border-collapse: collapse;
}

.compact-table th,
.compact-table td {
  padding: 14px 16px;
  border: 1px solid #dee2e6;
  font-size: 16px;
}

.compact-table th {
  background: #c3cdd3;
  font-weight: 700;
  position: sticky;
  top: 0;
  white-space: nowrap;
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.compact-table td {
  white-space: nowrap;
}

.compact-table tbody tr:hover {
  background: #e0e7ff !important;
}

.table-success-row {
  background: linear-gradient(90deg, rgba(16, 185, 129, 0.05), transparent);
}

.table-danger-row {
  background: linear-gradient(90deg, rgba(239, 68, 68, 0.05), transparent);
}

.totals-row {
  background: #fef3c7;
  font-weight: 600;
}

.totals-row td {
  background: #fef3c7;
}

.text-right {
  text-align: right !important;
}

.text-center {
  text-align: center !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-success {
  color: #28a745 !important;
}

.text-tax-exempt {
  color: #6f42c1 !important;
}

.delivery-badge {
  font-size: 14px;
  padding: 4px 12px;
}

.sort-btn {
  text-decoration: none;
}

.sort-btn:hover {
  text-decoration: none;
}

.pagination-wrapper {
  border-top: 1px solid #e2e8f0;
}

/* =========================================================
   ANIMATIONS
   ========================================================= */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.kpi-card, .data-table-card, .filters-card {
  animation: fadeIn 0.5s ease-out;
}

.border-top {
  border-top: 1px solid #e2e8f0;
}

/* =========================================================
   RESPONSIVE
   ========================================================= */
@media (max-width: 1400px) {
  .kpi-grid { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 992px) {
  .dashboard-container { padding: 16px; }
  .header-title { font-size: 24px; }
  .kpi-grid { grid-template-columns: repeat(2, 1fr); gap: 16px; }
  .kpi-value { font-size: 32px; }
  .kpi-icon-wrap { width: 64px; height: 64px; font-size: 28px; margin-bottom: 16px; }
  .kpi-card { padding: 28px 18px 22px; }
  .compact-table th, .compact-table td { padding: 10px 12px; font-size: 14px; }
  .table-title { font-size: 16px; }
}

@media (max-width: 576px) {
  .dashboard-container { padding: 10px; }
  .kpi-grid { grid-template-columns: 1fr; }
  .kpi-value { font-size: 28px; }
  .kpi-label { font-size: 15px; }
  .kpi-trend { font-size: 13px; padding: 6px 12px; }
  .compact-table th, .compact-table td { padding: 8px 10px; font-size: 12px; }
  .dashboard-header h5 { font-size: 18px; }
  .header-subtitle { font-size: 12px; }
}
</style>


// WEBPACK FOOTER //
// src/components/Reports/ReportsStoresSale.vue